import { useTranslation } from 'react-i18next';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: '什么是"反差设计"？它在实际创作中有多重要？',
    answer: '反差设计是指通过色彩、明暗、大小、疏密、风格等元素的强烈对比，在视觉或内容中制造张力与冲击力的设计方法。在实际创作中，反差是最有效的"注意力捕获器"——人的视觉系统天生对变化和对比敏感，合理的反差运用可以让作品在0.2秒内抓住观众的眼球，在信息爆炸的时代，这0.2秒往往决定了作品是被记住还是被划过。',
  },
  {
    question: '反差设计和"对比度"有什么不同？',
    answer: '对比度是技术参数，反差是设计哲学。对比度仅描述亮度差异的数值，而反差涵盖了色彩、情绪、风格、叙事等多个维度的对立与统一。简单来说，对比度是反差的子集——一个人的照片调高对比度可以让画面更锐利，但真正的反差设计要做到的是：柔和的画面中突然出现一道锐利的光，安静的构图中藏着一处暴烈的色彩。',
  },
  {
    question: '如何在文案写作中运用反差技巧？',
    answer: '文案反差的核心是"预期违背"。具体技巧包括：大小对比法（大投入对小产出）、身份翻转法（看似矛盾的身份排列）、价格反差法（用熟悉低价参照物锚定高价服务）、否定前置法（先说"不是"，再引出"是"）。关键是在读者产生"理所当然"的阅读预期时，用一个意外的转折打破它——这种"意料之外，情理之中"的体验，就是文案反差的精髓。',
  },
  {
    question: '反差营销适合所有品牌吗？会不会有翻车的风险？',
    answer: '反差营销不是万能药。它最适合那些行业共识已经固化、消费者产生审美疲劳的品类——比如茶饮、汽车、玩具等。但反差营销确实存在翻车风险，核心在于"反差"必须建立在品牌真实基因之上，而非凭空制造。蜜雪冰城的"土味"反差之所以成功，是因为它本来就是一个走下沉路线的品牌；如果一个奢侈品牌突然走"土味"路线，那就不叫反差营销，叫品牌自杀。',
  },
  {
    question: '初学者从哪里开始学习反差设计？',
    answer: '建议从"明度反差"开始——这是最直观、最容易感知的反差维度。试着把一张照片调成黑白，观察去掉色彩之后的光影关系。然后进阶到"色彩反差"——在色相环上找到对立色进行组合实验。最后再探索"风格反差"——将极简与极繁、传统与现代、东方与西方进行碰撞。每一步都要记住：反差的目的是服务表达，不是为了反差而反差。',
  },
];

export default function HomeFAQ() {
  const { t } = useTranslation();

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            常见问题
          </h2>
          <p className="text-foreground-500 mt-3 text-sm md:text-base">
            关于反差设计的那些事儿，你关心的都在这里
          </p>
        </div>
        <div className="space-y-3">
          {faqData.map((item, index) => (
            <details
              key={index}
              className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden"
            >
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                  {item.question}
                </h3>
                <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-background-100 group-open:bg-primary-100 transition-colors">
                  <i className="ri-add-line text-foreground-500 group-open:hidden text-sm"></i>
                  <i className="ri-subtract-line text-primary-600 hidden group-open:block text-sm"></i>
                </span>
              </summary>
              <div className="px-5 pb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}